/**
 * Pagination — « 1–12 sur 205 » à gauche, les pages à droite : précédente,
 * au plus sept cases (« 1 … 8 9 10 … 18 »), suivante. La page courante est
 * une pilule à la marque (un accent d'interaction), les autres en gris.
 *
 *   <Pagination page={page} pages={18} onPage={setPage} total={205} parPage={12} />
 *
 * C'est le pied du `Tableau`, sorti pour servir seul (une grille de cartes,
 * une liste) — le Tableau le rend à son tour. Successeur de `TablePagination`
 * (`panels/creators/orders/tableBits.tsx`) et du `Pager` de Helios. Lot 2 de
 * la refonte du catalogue, 28-10-2026.
 */
import { Button } from "./Button";
import { ChevronLeft, ChevronRight } from "./icons";
import { useLibelles } from "./libelles";
import { pagesAffichees } from "./tableau/pagination";
import { formatNombre } from "./tableau/formats";
import { cn } from "./utils";
import "./lecture.css";

export interface PaginationProps {
  /** La page courante, à partir de 1. */
  page: number;
  pages: number;
  onPage: (page: number) => void;
  /** Avec `parPage` : écrit « 1–12 sur 205 » à gauche. Sans : les pages seules. */
  total?: number;
  parPage?: number;
  taille?: "sm" | "md";
  /** Le nom de la navigation et les deux flèches, pour les lecteurs d'écran. Défaut : les libellés de la
   *  librairie (`<Libelles>`, français sans rien) — une prop donnée l'emporte. */
  nom?: string;
  precedent?: string;
  suivant?: string;
  /** Le mot entre les bornes et le total (« sur »). Même défaut. */
  sur?: string;
  className?: string;
}

export function Pagination({
  page,
  pages,
  onPage,
  total,
  parPage,
  taille = "md",
  nom: nomDonne,
  precedent: precedentDonne,
  suivant: suivantDonne,
  sur: surDonne,
  className,
}: PaginationProps) {
  const l = useLibelles().pagination;
  const nom = nomDonne ?? l.nom;
  const precedent = precedentDonne ?? l.precedent;
  const suivant = suivantDonne ?? l.suivant;
  const sur = surDonne ?? l.sur;
  const n = Math.max(1, pages);
  const p = Math.min(Math.max(1, page), n);
  let compte: string | null = null;
  if (total !== undefined && parPage !== undefined) {
    const debut = (p - 1) * parPage;
    compte = total === 0 ? "0" : `${formatNombre(debut + 1)}–${formatNombre(Math.min(debut + parPage, total))} ${sur} ${formatNombre(total)}`;
  }
  return (
    <div className={cn("pg", className)} data-taille={taille}>
      {compte !== null && <span className="pg-compte">{compte}</span>}
      <nav className="pg-pages" aria-label={nom}>
        <Button variant="ghost" size="sm" iconOnly icon={<ChevronLeft className="h-3.5 w-3.5" />} disabled={p <= 1} onClick={() => onPage(p - 1)} aria-label={precedent} />
        {pagesAffichees(p, n).map((x, i) =>
          x === null ? (
            <span key={`e${i}`} className="pg-ellipse">
              …
            </span>
          ) : (
            <button
              key={x}
              type="button"
              className={cn("pg-page lc-pilule", x === p && "is-active")}
              onClick={() => onPage(x)}
              aria-current={x === p ? "page" : undefined}
            >
              {x}
            </button>
          ),
        )}
        <Button variant="ghost" size="sm" iconOnly icon={<ChevronRight className="h-3.5 w-3.5" />} disabled={p >= n} onClick={() => onPage(p + 1)} aria-label={suivant} />
      </nav>
    </div>
  );
}
